// ReportFilters — THE filter bar for every report ReportView renders (Executive Summary, Portfolio Tax Trend,
// Outstanding Appeals, Appeal Results, Jurisdiction Deadlines). Built from definition.filters; no report-specific code.
//
// (Philip, 2026-10-11)
//   • Nothing selected = all. Every chip starts OFF and the report shows everything; a click selects only what is
//     clicked, more clicks add, a second click removes. "Clear" on a row resets it to all.
//   • Options cascade down the page (viewModel.facetOptions): a filter offers only the values the filters above it leave.
//   • `picker: 'search'` (Property) is a searchable multi-select instead of a chip wall — clients run to 200+.
//   • Years are unchanged: one or more years, or All years.
// The selection logic is pure and lives in ../reports/viewModel; this file is the JSX over it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { facetOptions, NONE } from '../reports/viewModel';

function Bubble({ label, active, onClick, title, muted }) {
  return (
    <button type="button" onClick={onClick} title={title}
      style={{
        padding: '4px 11px', fontSize: '12px', borderRadius: '999px', cursor: 'pointer',
        border: `1px solid ${active ? 'var(--red)' : '#d4d0c8'}`,
        background: active ? 'rgba(180,0,0,0.08)' : '#ffffff',
        color: active ? 'var(--red)' : '#5a5648', fontWeight: active ? 700 : 500,
        textDecoration: muted ? 'line-through' : 'none', opacity: muted ? 0.7 : 1,
      }}>{label}</button>
  );
}

const lbl = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a8578', minWidth: '54px' };
const row = { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' };
const clearLink = { background: 'none', border: 'none', padding: '0 4px', fontSize: '11px', color: 'var(--red)', cursor: 'pointer', textDecoration: 'underline' };
const UNAVAILABLE_TITLE = 'Selected, but not in the rows the other filters leave — click to remove';

// The searchable multi-select: type to narrow, tick to choose, "Select all matching", chosen items as removable tags,
// and a count against the options available. `options` come from facetOptions (unavailable picks included, marked).
export function SearchMultiSelect({ options, selected, onChange, noun = 'items' }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const sel = new Set(selected);
  const available = options.filter((o) => !o.unavailable);
  const needle = q.trim().toLowerCase();
  const matching = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const toggle = (v) => onChange(sel.has(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const addMatching = () => onChange([...selected, ...matching.filter((o) => !o.unavailable && !sel.has(o.value)).map((o) => o.value)]);
  const labelOf = (v) => (options.find((o) => o.value === v) || { label: v }).label;
  const pickedAvailable = available.filter((o) => sel.has(o.value)).length;

  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 320px', minWidth: 0 }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder={`Search ${noun}…`} aria-label={`Search ${noun}`}
          style={{ padding: '5px 9px', fontSize: '12px', borderRadius: '8px', border: '1px solid #d4d0c8', width: '260px', maxWidth: '100%' }} />
        <span style={{ fontSize: '12px', color: '#8a8578' }}>
          {selected.length ? `${pickedAvailable} of ${available.length} ${noun}` : `All ${available.length} ${noun}`}
        </span>
      </div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {selected.map((v) => {
            const off = options.find((o) => o.value === v)?.unavailable;
            return (
              <span key={v} title={off ? UNAVAILABLE_TITLE : undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 4px 2px 9px', fontSize: '12px', borderRadius: '999px',
                  border: '1px solid var(--red)', background: 'rgba(180,0,0,0.08)', color: 'var(--red)', fontWeight: 600,
                  textDecoration: off ? 'line-through' : 'none', opacity: off ? 0.7 : 1 }}>
                {labelOf(v)}
                <button type="button" aria-label={`Remove ${labelOf(v)}`} onClick={() => toggle(v)}
                  style={{ border: 'none', background: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 3px' }}>×</button>
              </span>
            );
          })}
        </div>
      )}
      {open && (
        <div style={{ position: 'absolute', top: '34px', left: 0, zIndex: 60, background: '#fff', border: '1px solid #e6e2da', borderRadius: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: '360px', maxWidth: '90vw' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid #f0ede7' }}>
            <button type="button" style={{ ...clearLink, padding: 0 }} onClick={addMatching}
              disabled={!matching.some((o) => !o.unavailable && !sel.has(o.value))}>
              Select all matching ({matching.filter((o) => !o.unavailable).length})
            </button>
            <button type="button" style={{ ...clearLink, padding: 0, color: '#5a5648' }} onClick={() => setOpen(false)}>Done</button>
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', padding: '4px 0' }}>
            {matching.length ? matching.map((o) => (
              <label key={o.value} title={o.unavailable ? UNAVAILABLE_TITLE : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', fontSize: '13px', cursor: 'pointer',
                  color: o.unavailable ? '#a9a396' : '#3c3a33' }}>
                <input type="checkbox" checked={sel.has(o.value)} onChange={() => toggle(o.value)} />
                <span style={{ textDecoration: o.unavailable ? 'line-through' : 'none' }}>{o.label}</span>
              </label>
            )) : <div style={{ padding: '6px 12px', fontSize: '12px', color: '#8a8578' }}>No {noun} match “{q}”.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// The Notices chips (step 8): Current (the default), Original, each active Notice Type (from the table, rows or not), All. Several at once;
// All stands for everything; the last chip can't be switched off (a report of no notices is a blank page).
function nextNoticePick(sel, value) {
  if (value === 'all') return sel.includes('all') ? ['current'] : ['all'];
  const base = sel.filter((v) => v !== 'all');
  const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
  return next.length ? next : sel;
}

// props: filters (normalised), definition, rows (the built, unfiltered rows), state, onEnum(key, values),
// onToggle(key, value), onSearch(key, text); years: { show, configYears, years, allYears, setYears, setAllYears };
// noticeTypes: the active notice types [{ key, name }] for a 'notices' filter.
// A note keeps its own text while typing and hands it over on leaving the box (it is saved with the report).
function NoteBox({ f, value, onNote }) {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  return (
    <div style={{ ...row, alignItems: 'flex-start' }}>
      <span style={{ ...lbl, paddingTop: '6px' }}>{f.label}</span>
      <textarea value={text} rows={f.lines || 1} placeholder={f.placeholder || ''} onChange={(e) => setText(e.target.value)}
        onBlur={() => { if (text !== (value || '')) onNote(f.key, text); }}
        style={{ padding: '5px 9px', fontSize: '12px', borderRadius: '8px', border: '1px solid #d4d0c8', minWidth: '420px', fontFamily: 'inherit' }} />
    </div>
  );
}

export default function ReportFilters({ filters: allFilters, definition, rows, state, onEnum, onToggle, onSearch, onNote, years, noticeTypes = [] }) {
  const filters = allFilters.filter((f) => !f.showWhen || f.showWhen(state || {}));
  const enums = filters.filter((f) => f.type === 'enum');
  const optionsByKey = useMemo(
    () => Object.fromEntries(enums.map((f) => [f.key, facetOptions(rows, definition, state, f.key)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, definition, state]);

  return (
    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {years?.show && (
        <div style={row}>
          <span style={lbl}>Years</span>
          <Bubble label="All years" active={years.allYears} onClick={() => years.setAllYears((a) => !a)} />
          {!years.allYears && years.configYears.map((y) => (
            <Bubble key={y} label={y} active={years.years.includes(y)}
              onClick={() => years.setYears((ys) => (ys.includes(y) ? ys.filter((x) => x !== y) : [...ys, y].sort()))} />
          ))}
        </div>
      )}
      {enums.map((f) => {
        const opts = optionsByKey[f.key] || [];
        const sel = Array.isArray(state[f.key]) ? state[f.key] : [];
        // Nothing to choose: no values, or every row blank there (only "(None)") — a chip that can't narrow anything.
        if (!opts.length || (opts.length === 1 && opts[0].value === NONE && !sel.length)) return null;
        const clear = sel.length > 0 && <button type="button" style={clearLink} onClick={() => onEnum(f.key, [])}>Clear</button>;
        if (f.picker === 'search') {
          return (
            <div key={f.key} style={{ ...row, alignItems: 'flex-start' }}>
              <span style={{ ...lbl, paddingTop: '6px' }}>{f.label}</span>
              <SearchMultiSelect options={opts} selected={sel} noun={f.noun || f.label.toLowerCase()} onChange={(v) => onEnum(f.key, v)} />
              <span style={{ paddingTop: '5px' }}>{clear}</span>
            </div>
          );
        }
        return (
          <div key={f.key} style={row}>
            <span style={lbl}>{f.label}</span>
            {opts.map((o) => (
              <Bubble key={o.value} label={o.label} active={sel.includes(o.value)} muted={o.unavailable}
                title={o.unavailable ? UNAVAILABLE_TITLE : o.value === NONE ? 'Rows with no value here' : undefined}
                onClick={() => onEnum(f.key, sel.includes(o.value) ? sel.filter((v) => v !== o.value) : [...sel, o.value])} />
            ))}
            {clear}
          </div>
        );
      })}
      {filters.filter((f) => f.type === 'notices').map((f) => {
        const sel = Array.isArray(state[f.key]) && state[f.key].length ? state[f.key] : (f.default || []);
        // Current (the default): each year's value, a Corrected notice replacing the original; Original: as entered.
        const chips = [{ value: 'current', label: 'Current' }, { value: 'original', label: 'Original' },
          ...noticeTypes.map((t) => ({ value: t.key, label: t.name })), { value: 'all', label: 'All' }];
        return (
          <div key={f.key} style={row}>
            <span style={lbl}>{f.label}</span>
            {chips.map((o) => (
              <Bubble key={o.value} label={o.label} active={sel.includes(o.value)} onClick={() => onEnum(f.key, nextNoticePick(sel, o.value))} />
            ))}
          </div>
        );
      })}
      {filters.filter((f) => f.type === 'toggle').map((f) => (
        <div key={f.key} style={row}>
          <span style={lbl}>{f.label}</span>
          {(f.options || []).map((o) => (
            <Bubble key={o.value} label={o.label} active={state[f.key] === o.value} onClick={() => onToggle(f.key, o.value)} />
          ))}
        </div>
      ))}
      {filters.filter((f) => f.type === 'note').map((f) => <NoteBox key={f.key} f={f} value={state[f.key]} onNote={onNote} />)}
      {filters.filter((f) => f.type === 'search').map((f) => (
        <div key={f.key} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={lbl}>{f.label}</span>
          <input value={state[f.key] || ''} onChange={(e) => onSearch(f.key, e.target.value)}
            placeholder={f.placeholder || 'Search…'}
            style={{ padding: '5px 9px', fontSize: '12px', borderRadius: '8px', border: '1px solid #d4d0c8', minWidth: '220px' }} />
        </div>
      ))}
    </div>
  );
}
